/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {MouseEvent} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  ListItem,
  makeStyles,
  withStyles
} from '@material-ui/core';
import {getAssetState} from '@ion/core/src/entities/Asset';
import {translate} from '@ion/core/src/locale/locale';
import {createRegExp} from '@ion/core/src/utils/StringUtil';
import AssetStatusItem from '@ion/dls/src/components/sidepanel/AssetStatusItem';
import {SearchResultsCurrentDestinationIcon} from '@ion/dls/src/icons/SearchResultsCurrentDestinationIcon';
import {dlsAlt, dlsColor, dlsLayout, dlsTypography} from '@ion/dls/src/theme/theme';
import {IconSize} from '@ion/dls/src/types/types';
import {BodyTypography} from '@ion/dls/src/typography/BodyTypography';
import {HeaderTypography} from '@ion/dls/src/typography/HeaderTypography';
import {LabelTypography} from '@ion/dls/src/typography/LabelTypography';
import {formatAddress} from '@ion/dls/src/utils/ComponentUtils';
import changePage, {getRoute, Route} from '@/routes/RouteDefinitions';
import {setPage, setSideSheetStatus} from '@/store/actions/nav';
import {initializeTracking} from '@/store/actions/tracking';
import {
  setAssetDetailView,
  setSelectedAsset
} from '../store/actions/tracking';

export interface SearchResultsProps {
  result: any;
  searchTerm: string;
  setDialogOpen: Function;
}

const useStyles = makeStyles(theme => ({
  root : {
    width         : '100%',
    paddingBottom : '2%',
    paddingTop    : '1%'
  },
  left : {
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'space-between',
    marginTop      : '6px'
  },
  right : {
    display    : 'flex',
    alignItems : 'center'
  },
  type : {
    display         : 'flex',
    alignItems      : 'center',
    color           : `${dlsColor('black')} !important`,
    backgroundColor : dlsColor('grey6')
  },
  bodyStyle1 : {
    ...dlsTypography('body3')
  },
  vehicleName : {
    ...dlsAlt('components', 'searchResults', 'vehiclelabel')
  },
  contentData : {
    display   : 'flex',
    marginTop : '1%'
  },
  description : {
    ...dlsTypography('body1'),
    marginBottom : '-6px',
    marginTop    : -(dlsLayout('spacing') - 4)
  },
  valueText : {
    marginLeft : '6px',
    color      : dlsColor('grey3')
  },
  markedText : {
    backgroundColor : dlsColor('paleBlue')
  },
  section : {
    color : dlsColor('black')
  }
}));

const StyledListItem = withStyles(theme => ({
  root : {
    '&:hover' : {
      backgroundColor : `${dlsAlt('components', 'assetCard', 'paleBlue2')}`
    }
  }
}))(ListItem);

const getResultName = (result: any) => {
  const resultName = result.name || result.assetName || result.landmarkName;
  if (!resultName) {
    if (result.lastName || result.firstName) {
      return (result.lastName || '' + ',' + result.firstName || '').replace(',', ', ');
    }
    return translate('common:notAvailable');
  }
  return resultName;
};

const getOperatorName = (result: any) => {
  if (result?.operatorNames) {
    return result.operatorNames.replace(',', ', ');
  }
  return translate('common:notAvailable');
};

export const SearchResult = ({
  result,
  searchTerm,
  setDialogOpen
}: SearchResultsProps) => {
  const classes  = useStyles();
  const dispatch = useDispatch();

  const isAsset = Boolean(result.assetId);

  const status = isAsset ? getAssetState(result) : null;

  function handleListItemClick(e: MouseEvent<HTMLSpanElement>, result: any) {
    dispatch(setPage(getRoute(Route.TRACKING)));
    changePage(Route.TRACKING);
    dispatch(initializeTracking());
    dispatch(setSelectedAsset({asset: result}) as any);
    dispatch(setAssetDetailView({value: true}) as any);
    dispatch(setSideSheetStatus({
      activeSideSheet      : '/tracking',
      desktopSideSheetOpen : true
    }));
    setDialogOpen(false);
  };

  const resultName        = getResultName(result);
  const resultDescription = result.description || '';

  function getHighlightedText(text, search) {
    return text?.split(createRegExp(`(${search})`, 'gi'));
  };

  const parts     = getHighlightedText(resultName, searchTerm);
  const descParts = getHighlightedText(resultDescription, searchTerm);

  return (
    <div>
      <StyledListItem disableRipple
        button
        onClick={function(e) {
          handleListItemClick(e, result);
        }}>
        <div className={classes.root}>
          <div className={classes.left}>
            <div className={classes.right}>
              <div style={{marginRight: '12px'}}>
                <HeaderTypography variant='h4' className={classes.vehicleName}>
                  {parts?.map(part =>
                    part.toLowerCase() === searchTerm.toLowerCase()
                      ? <span className={classes.markedText} key={part}>{part}</span>
                      : part)}
                </HeaderTypography>
              </div>
              {status &&
                <div className={classes.bodyStyle1}>
                  <AssetStatusItem assetStatus={status}
                    value={status} />
                </div>}
            </div>
            <div className={classes.type}>
              <LabelTypography className={classes.section}>
                {resultName ? translate('common:search.tracking') : translate('common:search.driver')}
              </LabelTypography>
            </div>
          </div>
          <div className={classes.contentData}>
            <div style={{marginRight: '6%'}}>
              <BodyTypography variant='body2' className={classes.bodyStyle1}>
                {getOperatorName(result)}
              </BodyTypography>
              <BodyTypography variant='body2' className={classes.bodyStyle1}>
                {result.assets?.length ? `${result.assets.length} ${translate('common:search.items')}` : ''}
              </BodyTypography>
            </div>
            <div style={{marginTop: '8px'}}>
              <SearchResultsCurrentDestinationIcon size={IconSize.large} />
            </div>
            <div style={{marginTop: '-17px'}}>
              <p className={classes.bodyStyle1}>{translate('common:search.current')}:
                <span className={classes.valueText}>{formatAddress(result)}</span>
              </p>
              <p className={classes.bodyStyle1}>{translate('common:search.destination')}:
                <span className={classes.valueText}>{translate('common:notAvailable')}</span>
              </p>
            </div>
          </div>
          <div>
            <p className={classes.description}>
              {descParts.map(part =>
                part.toLowerCase() === searchTerm.toLowerCase()
                  ? <span className={classes.markedText}>{part}</span>
                  : part)}
            </p>
          </div>
        </div>
      </StyledListItem>
    </div>
  );
};

export default SearchResult;
